import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import { useCurrentUserId } from '../../hooks/useCurrentUserId';
import { useFlowsApi } from '../../hooks/useFlowsApi';
import FlowCanvas from '../../components/Automations/FlowCanvas';
import QuotaIndicator from '../../components/Automations/QuotaIndicator';
import NodePalette from '../../components/Automations/NodePalette';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[6]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing[4]};
  }
`;

const BackButton = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  padding: ${({ theme }) => theme.spacing[2]};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary}10;
  }
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  flex: 1;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }
`;

const NewFlowButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.textPrimary};
  border: none;
  border-radius: ${({ theme }) => theme.borders.radius.md};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryHover};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const Content = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const Sidebar = styled(motion.div)`
  flex: 0 0 350px;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  overflow-y: auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex: 0 0 auto;
    max-height: 40vh;
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const FlowsList = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const FlowCard = styled(motion.div)<{ $active?: boolean }>`
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  padding: ${({ theme }) => theme.spacing[4]};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const FlowName = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing[1]} 0;
`;

const FlowDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
`;

const FlowActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[3]};
`;

const ActionButton = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const MainArea = styled(motion.div)`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing[6]};
`;

const EmptyState = styled.div`
  text-align: center;
  max-width: 400px;
`;

const EmptyStateTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
`;

const EmptyStateText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  line-height: 1.6;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${({ theme }) => theme.colors.border};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const GmailAutomation: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useCurrentUserId();
  const [selectedFlowId, setSelectedFlowId] = useState<string | null>(null);
  
  const { flows, isLoading, createFlow, executeFlow, isExecuting } = useFlowsApi('gmail', userId || undefined);
  
  const handleCreateFlow = async () => {
    if (!userId) return;
    
    await createFlow({
      name: 'Novo Fluxo Gmail',
      description: 'Descrição do fluxo de automação',
      nodes: [],
      connections: [],
      isActive: true,
      channel: 'gmail',
    });
  };
  
  const handleExecuteFlow = async (flowId: string) => {
    if (!userId) return;
    
    await executeFlow({ flowId, context: {} });
  };
  
  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/automations')}>
          <ArrowLeft size={20} />
        </BackButton>
        <Title>Automação Gmail</Title>
        <NewFlowButton onClick={handleCreateFlow}>
          <Plus size={16} />
          Novo Fluxo
        </NewFlowButton>
      </Header>
      
      <Content>
        {/* NodePalette - Sidebar com componentes arrastáveis */}
        <NodePalette />
        
        <Sidebar
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Quota Indicator */}
          {userId && <QuotaIndicator userId={userId} />}
          
          {isLoading ? (
            <FlowsList>
              <LoadingState>
                <Spinner />
                <p>Carregando fluxos...</p>
              </LoadingState>
            </FlowsList>
          ) : (
            <FlowsList>
              {flows.map((flow) => (
                <FlowCard
                  key={flow.id}
                  $active={selectedFlowId === flow.id}
                  onClick={() => setSelectedFlowId(flow.id)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FlowName>{flow.name}</FlowName>
                  <FlowDescription>{flow.description}</FlowDescription>
                  
                  <FlowActions>
                    <ActionButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExecuteFlow(flow.id);
                      }}
                      disabled={isExecuting}
                    >
                      <Play size={12} />
                      Executar
                    </ActionButton>
                  </FlowActions>
                </FlowCard>
              ))}
            </FlowsList>
          )}
        </Sidebar>
        
        <MainArea
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          {!selectedFlowId ? (
            <EmptyState>
              <EmptyStateTitle>Nenhum fluxo selecionado</EmptyStateTitle>
              <EmptyStateText>
                Selecione um fluxo existente na barra lateral ou crie um novo para começar
              </EmptyStateText>
            </EmptyState>
          ) : (
            <FlowCanvas flowId={selectedFlowId} channel="gmail" userId={userId!} />
          )}
        </MainArea>
      </Content>
    </Container>
  );
};

export default GmailAutomation;
